import { svgIcons } from './svg_icons'

const LOCALIZED_TEXT = {
    editImage: document.querySelector('meta[name="localized-edit-image"]')?.getAttribute("content") || "Edit Image",
    close: document.querySelector('meta[name="localized-close"]')?.getAttribute("content") || "Close",
    brushTool: document.querySelector('meta[name="localized-brush-tool"]')?.getAttribute("content") || "Brush Tool",
    eraserTool: document.querySelector('meta[name="localized-eraser-tool"]')?.getAttribute("content") || "Eraser Tool",
    zoomIn: document.querySelector('meta[name="localized-zoom-in"]')?.getAttribute("content") || "Zoom In",
    zoomOut: document.querySelector('meta[name="localized-zoom-out"]')?.getAttribute("content") || "Zoom Out",
    rotateLeft: document.querySelector('meta[name="localized-rotate-left"]')?.getAttribute("content") || "Rotate Left",
    rotateRight: document.querySelector('meta[name="localized-rotate-right"]')?.getAttribute("content") || "Rotate Right",
    cancel: document.querySelector('meta[name="localized-cancel"]')?.getAttribute("content") || "Cancel",
    save: document.querySelector('meta[name="localized-save"]')?.getAttribute("content") || "Save",
    errorSaving: document.querySelector('meta[name="localized-error-saving"]')?.getAttribute("content") || "Error saving",
    undo: document.querySelector('meta[name="localized-undo"]')?.getAttribute("content") || "Undo",
    redo: document.querySelector('meta[name="localized-redo"]')?.getAttribute("content") || "Redo",
    textTool: document.querySelector('meta[name="localized-text-tool"]')?.getAttribute("content") || "Text Tool",
}

// Drawing Configuration
const BRUSH_SIZE = 5
const ERASER_SIZE = 20

// State tracking for undo/redo
const canvasStates = []
const redoStates = []

// Canvas Configuration
const CANVAS_CONFIG = {
    strokeStyle: '#000000',
    lineCap: 'round',
    lineJoin: 'round',
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high'
}

// Add text configuration
const TEXT_CONFIG = {
    font: '30px Arial',
    fillStyle: '#000000'
}

const createEditButton = () => {
    const button = document.createElement("button")
    button.className = "absolute top-2 right-2 p-2 rounded-lg shadow-sm transition-all action-item-button"
    button.innerHTML = svgIcons.edit
    return button
}

/**
 * Initializes editable functionality for images on the page.
 * Sets up mutation observer to handle dynamically added content.
 */
export const initializeEditableImages = () => {
    // Initial load
    setupEditableImages()

    // Setup MutationObserver for dynamic content
    const observer = new MutationObserver(() => {
        // If any new editable elements exist, set them up
        if (document.querySelectorAll('.editable').length) {
            setupEditableImages()
        }
    })

    // Start observing the document body for DOM changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    })
}

/**
 * Sets up editable functionality for images with the 'editable' class.
 * Adds edit buttons and click handlers to each image.
 */
const setupEditableImages = () => {
    document.querySelectorAll(".editable").forEach(editable => {
        // Skip if already initialized
        if (editable.dataset.editInitialized) return

        const imageElement = editable.querySelector("img")
        if (!imageElement) return

        const baseUrl = editable.closest("[data-url]")?.dataset?.url
        if (!baseUrl) return

        const imageWrapper = editable.querySelector(".relative")
        if (!imageWrapper) return

        const editButton = createEditButton()
        imageWrapper.appendChild(editButton)

        editButton.addEventListener("click", () => {
            handleEditBtnClick(imageElement, imageWrapper, baseUrl)
        })

        // Mark as initialized
        editable.dataset.editInitialized = 'true'
    })
}

const handleEditBtnClick = (imageElement, imageWrapper, baseUrl) => {
    const updateUrl = baseUrl.replace('/images', '/update_image')
    openImageEditPopup(imageElement.src, imageWrapper.dataset.imageId, updateUrl)
}

const createPopupElement = (imageUrl) => {
    const popup = document.createElement("div")
    popup.className = "editable-popup hidden overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)] max-h-full"

    popup.innerHTML = `
    <div class="relative p-4 w-full max-w-3xl max-h-full">
      <div class="relative bg-white rounded-lg shadow">
        <!-- Modal header -->
        <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t bg-primary-color">
          <h3 class="text-lg font-semibold text-light">
            ${LOCALIZED_TEXT.editImage}
          </h3>
          <button type="button" class="text-light hover:bg-secondary-color rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition-all" id="closePopup">
            ${svgIcons.close}
          </button>
        </div>

        <!-- Modal body -->
        <div class="p-4 md:p-5 space-y-4">
          <!-- Toolbar -->
          <div class="toolbar flex gap-2 justify-center border-b pb-4">
            <div class="flex gap-1 items-center bg-tertiary p-1 rounded-lg">
              <!-- Undo -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.undo}" data-tool="undo">
                ${svgIcons.undo}
              </button>
              <!-- Redo -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.redo}" data-tool="redo">
                ${svgIcons.redo}
              </button>
            </div>
            <div class="flex gap-1 items-center bg-tertiary p-1 rounded-lg">
              <!-- Brush Tool -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.brushTool}" data-tool="brush">
                ${svgIcons.brush}
              </button>
              <!-- Eraser Tool -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.eraserTool}" data-tool="eraser">
                ${svgIcons.eraser}
              </button>
              <!-- Text Tool -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.textTool}" data-tool="text">
                ${svgIcons.text}
              </button>
              <!-- Color Picker -->
              <input type="color" class="w-8 h-8 rounded cursor-pointer" value="#000000" title="Choose Color" id="colorPicker">
            </div>

            <div class="flex gap-1 items-center bg-tertiary p-1 rounded-lg">
              <!-- Zoom In -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.zoomIn}" data-tool="zoom-in">
                ${svgIcons.zoomIn}
              </button>
              <!-- Zoom Out -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.zoomOut}" data-tool="zoom-out">
                ${svgIcons.zoomOut}
              </button>
            </div>

            <div class="flex gap-1 items-center bg-tertiary p-1 rounded-lg">
              <!-- Rotate Left -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.rotateLeft}" data-tool="rotate-left">
                ${svgIcons.rotateLeft}
              </button>
              <!-- Rotate Right -->
              <button class="p-2 rounded hover:bg-white transition-all" title="${LOCALIZED_TEXT.rotateRight}" data-tool="rotate-right">
                ${svgIcons.rotateRight}
              </button>
            </div>
          </div>

          <!-- Image Container -->
          <div class="relative overflow-auto" style="max-height: 60vh;">
            <img src="${imageUrl}" class="max-w-full mx-auto transform-gpu" id="editableImage" alt="Edit image">
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-2">
            <button class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-all" id="cancelEdit">${LOCALIZED_TEXT.cancel}</button>
            <button class="px-4 py-2 bg-primary-color hover:bg-secondary-color text-light rounded transition-all" id="saveEdit">${LOCALIZED_TEXT.save}</button>
          </div>
        </div>
      </div>
    </div>
  `

    return popup
}

/**
 * Creates a canvas element and initializes its context for image editing.
 * @param {HTMLImageElement} editableImage - The image element to be edited
 * @param {HTMLElement} popup - The popup container element
 * @returns {Object} Object containing canvas and context
 */
const initializeCanvas = (editableImage) => {
    const canvas = document.createElement('canvas')
    canvas.className = 'absolute top-0 left-0'
    const ctx = canvas.getContext('2d')

    // Set up initial brush configuration
    Object.assign(ctx, CANVAS_CONFIG)
    ctx.lineWidth = BRUSH_SIZE
    ctx.globalCompositeOperation = 'source-over'

    editableImage.crossOrigin = "anonymous"

    // Wait for image to load before setting up canvas
    editableImage.onload = () => {
        canvas.width = editableImage.naturalWidth
        canvas.height = editableImage.naturalHeight
        canvas.style.width = '100%'
        canvas.style.height = '100%'

        const imageContainer = editableImage.parentElement
        imageContainer.style.position = 'relative'
        imageContainer.appendChild(canvas)

        // Save initial blank state
        saveState(canvas)

        // Initialize brush tool
        const brushTool = document.querySelector('[data-tool="brush"]')
        if (brushTool) {
            highlightSelectedTool(brushTool)
            setupBrushTool(ctx)
        }
    }

    return {canvas, ctx}
}

/**
 * Saves the current canvas state to the undo history and clears redo history.
 * @param {HTMLCanvasElement} canvas - The canvas element to save
 */
const saveState = (canvas) => {
    const state = canvas.toDataURL()
    if (state !== canvasStates[canvasStates.length - 1]) {
        canvasStates.push(state)
        // Clear redo states when new action is performed
        redoStates.length = 0
    }
}

/**
 * Sets up mouse event handlers for drawing on the canvas.
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 */
const setupDrawingHandlers = (canvas, ctx) => {
    let isDrawing = false

    const startDrawing = (e) => {
        isDrawing = true
        draw(e, canvas, ctx, isDrawing)
    }

    const stopDrawing = () => {
        isDrawing = false
        saveState(canvas)
        ctx.beginPath()
    }

    canvas.addEventListener('mousedown', startDrawing)
    canvas.addEventListener('mousemove', (e) => draw(e, canvas, ctx, isDrawing))
    canvas.addEventListener('mouseup', stopDrawing)
    canvas.addEventListener('mouseout', stopDrawing)
}

/**
 * Handles drawing on the canvas based on mouse position.
 * @param {MouseEvent} e - The mouse event
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {boolean} isDrawing - Whether drawing is currently active
 */
const draw = (e, canvas, ctx, isDrawing) => {
    if (!isDrawing) return

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY

    ctx.lineTo(x, y)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(x, y)
}

/**
 * Sets up click handlers for the editing tools (brush, eraser, zoom, rotate).
 * @param {HTMLElement} popup - The popup container element
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {HTMLImageElement} editableImage - The image being edited
 */
const setupToolHandlers = (popup, ctx, editableImage) => {
    const state = {
        currentRotation: 0,
        currentScale: 1
    }

    // Add color picker handler
    const colorPicker = popup.querySelector('#colorPicker')
    colorPicker.addEventListener('input', (e) => {
        CANVAS_CONFIG.strokeStyle = e.target.value
        if (ctx.globalCompositeOperation === 'source-over') {
            ctx.strokeStyle = e.target.value
        }
    })

    popup.addEventListener('click', (e) => {
        const tool = e.target.closest('[data-tool]')
        if (!tool) return

        handleToolClick(tool, ctx, editableImage, state)
    })
}

/**
 * Handles tool selection and applies the appropriate action.
 * @param {HTMLElement} tool - The selected tool element
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {HTMLImageElement} editableImage - The image being edited
 * @param {Object} state - Current transformation state
 */
const handleToolClick = (tool, ctx, editableImage, state) => {
    switch (tool.dataset.tool) {
        case 'brush':
            setupBrushTool(ctx)
            break
        case 'eraser':
            setupEraserTool(ctx)
            break
        case 'zoom-in':
            state.currentScale = Math.min(state.currentScale + 0.1, 3)
            updateImageTransform(editableImage, state.currentRotation, state.currentScale)
            break
        case 'undo':
            undo(ctx)
            break
        case 'redo':
            redo(ctx)
            break
        case 'zoom-out':
            state.currentScale = Math.max(state.currentScale - 0.1, 1)
            updateImageTransform(editableImage, state.currentRotation, state.currentScale)
            break
        case 'rotate-left':
            state.currentRotation = (state.currentRotation - 90) % 360
            updateImageTransform(editableImage, state.currentRotation, state.currentScale)
            break
        case 'rotate-right':
            state.currentRotation = (state.currentRotation + 90) % 360
            updateImageTransform(editableImage, state.currentRotation, state.currentScale)
            break
        case 'text':
            setupTextTool(ctx)
            break
    }
    highlightSelectedTool(tool)
}

const setupBrushTool = (ctx) => {
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = CANVAS_CONFIG.strokeStyle
    ctx.lineWidth = BRUSH_SIZE
}

const setupEraserTool = (ctx) => {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.lineWidth = ERASER_SIZE
}

/**
 * Reverts the canvas to its previous state.
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 */
const undo = (ctx) => {
    if (canvasStates.length > 1) {
        const currentState = canvasStates.pop()
        redoStates.push(currentState)
        const previousState = canvasStates[canvasStates.length - 1]

        const img = new Image()
        img.src = previousState
        img.onload = () => {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
            ctx.drawImage(img, 0, 0)
        }
    }
}

/**
 * Restores the most recently undone state.
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 */
const redo = (ctx) => {
    if (redoStates.length > 0) {
        const nextState = redoStates.pop()
        canvasStates.push(nextState)

        const img = new Image()
        img.src = nextState
        img.onload = () => {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
            ctx.drawImage(img, 0, 0)
        }
    }
}

/**
 * Updates the image transformation (rotation and scale).
 * @param {HTMLImageElement} editableImage - The image being transformed
 * @param {number} rotation - Rotation in degrees
 * @param {number} scale - Scale factor
 */
const updateImageTransform = (editableImage, rotation, scale) => {
    editableImage.style.transform = `rotate(${rotation}deg) scale(${scale})`
}

const setupSaveHandler = (popup, canvas, editableImage, imageId, updateUrl) => {
    popup.querySelector("#saveEdit").addEventListener("click", () => {
        saveEditedImage(popup, canvas, editableImage, imageId, updateUrl)
    })
}

const saveEditedImage = (popup, canvas, editableImage, imageId, updateUrl) => {
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    tempCanvas.width = editableImage.naturalWidth
    tempCanvas.height = editableImage.naturalHeight

    // Create a new image with crossOrigin set
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = editableImage.src

    img.onload = () => {
        // Draw original image and canvas content
        tempCtx.drawImage(img, 0, 0)
        tempCtx.drawImage(canvas, 0, 0)

        // Save the edited image
        const editedImageUrl = tempCanvas.toDataURL('image/png')
        saveToServer(editedImageUrl, imageId, updateUrl, popup)
    }
}

/**
 * Saves the edited image to the server.
 * @param {string} imageData - The edited image data URL
 * @param {string} imageId - The ID of the image being edited
 * @param {string} updateUrl - The URL to send the update request to
 * @param {HTMLElement} popup - The popup container element
 */
const saveToServer = (imageData, imageId, updateUrl, popup) => {
    fetch(updateUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({image_data: imageData, image_id: imageId})
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                window.location.reload()
                popup.remove()
            } else {
                throw new Error(data.error)
            }
        })
        .catch(error => {
            console.error('Error saving drawing:', error)
            renderErrorMessage(popup, LOCALIZED_TEXT.errorSaving)
        })
}

/**
 * Creates and opens the image editing popup.
 * @param {string} imageUrl - URL of the image to edit
 * @param {string} imageId - ID of the image
 * @param {string} updateUrl - URL for saving changes
 */
const openImageEditPopup = (imageUrl, imageId, updateUrl) => {
    const popup = createPopupElement(imageUrl)
    document.body.appendChild(popup)

    setTimeout(() => {
        popup.classList.remove('hidden')
        popup.classList.add('flex')
    }, 0)

    const editableImage = popup.querySelector('#editableImage')
    const {canvas, ctx} = initializeCanvas(editableImage)

    setupDrawingHandlers(canvas, ctx)
    setupToolHandlers(popup, ctx, editableImage)
    setupSaveHandler(popup, canvas, editableImage, imageId, updateUrl)

    // Setup close handlers
    const closePopup = () => popup.remove()
    popup.querySelector("#closePopup").addEventListener("click", closePopup)
    popup.querySelector("#cancelEdit").addEventListener("click", closePopup)
}

/**
 * Updates the visual state of the selected tool.
 * @param {HTMLElement} selectedTool - The tool element that was selected
 */
const highlightSelectedTool = (selectedTool) => {
    // Get the popup from the selected tool's ancestor
    const popup = selectedTool.closest('.relative.bg-white.rounded-lg.shadow')
    if (!popup) return

    // Remove highlight from all tools
    popup.querySelectorAll('[data-tool]').forEach(tool => {
        tool.classList.remove('bg-white')
    })
    // Add highlight to selected tool
    selectedTool.classList.add('bg-white')
}

/**
 * Displays an error message in the popup.
 * @param {HTMLElement} popup - The popup container element
 * @param {string} message - The error message to display
 */
const renderErrorMessage = (popup, message) => {
    const existingError = popup.querySelector('.error-message')
    if (existingError) existingError.remove()

    const errorMessage = document.createElement('div')
    errorMessage.className = 'error-message bg-red-100 border border-red-400 text-red-700 px-4 py-2 mb-4 rounded'
    errorMessage.textContent = message

    const toolbar = popup.querySelector('.toolbar')
    toolbar.parentNode.insertBefore(errorMessage, toolbar)
}

// Add new function for text tool setup
const setupTextTool = (ctx) => {
    const canvas = ctx.canvas
    canvas.style.cursor = 'text'

    // Remove existing listeners
    canvas.removeEventListener('mousedown', canvas.drawHandler)
    canvas.removeEventListener('mousemove', canvas.moveHandler)
    canvas.removeEventListener('mouseup', canvas.endHandler)
    canvas.removeEventListener('mouseout', canvas.endHandler)

    // Create text input element
    const createTextInput = (x, y) => {
        const input = document.createElement('input')
        input.type = 'text'
        input.className = 'absolute bg-transparent border border-gray-400 px-1'
        input.style.position = 'absolute'
        input.style.left = `${x}px`
        input.style.top = `${y - 30}px`

        // Get the current color from the color picker
        const colorPicker = canvas.closest('.editable-popup').querySelector('#colorPicker')
        const currentColor = colorPicker ? colorPicker.value : TEXT_CONFIG.fillStyle

        input.style.color = currentColor
        TEXT_CONFIG.fillStyle = currentColor

        input.style.font = TEXT_CONFIG.font
        input.style.width = 'auto'
        input.style.minWidth = '100px'
        input.style.maxWidth = '200px'
        input.style.zIndex = '1000'
        input.style.boxSizing = 'border-box'
        input.style.lineHeight = '30px'

        // Add the input to the canvas container
        const container = canvas.parentElement
        container.style.position = 'relative'
        container.appendChild(input)

        // Focus after a brief delay to ensure proper placement
        setTimeout(() => input.focus(), 0)
        return input
    }

    // Handle text input
    const handleTextInput = (input, startX, startY) => {
        const handleEnter = () => {
            const text = input.value
            if (text) {
                // Calculate the actual canvas coordinates
                const rect = canvas.getBoundingClientRect()
                const scaleX = canvas.width / rect.width
                const scaleY = canvas.height / rect.height
                const canvasX = startX * scaleX
                const canvasY = startY * scaleY + (30 * scaleY)

                // Draw text on canvas
                ctx.font = TEXT_CONFIG.font
                ctx.fillStyle = TEXT_CONFIG.fillStyle
                ctx.textBaseline = 'alphabetic'
                ctx.fillText(text, canvasX, canvasY)

                // Save state and cleanup
                saveState(canvas)
            }
            input.remove()

            // Switch back to brush tool
            const popup = canvas.closest('.editable-popup')
            const brushTool = popup.querySelector('[data-tool="brush"]')
            if (brushTool) {
                brushTool.click()
            }
        }

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleEnter()
            } else if (e.key === 'Escape') {
                input.remove()
            }
        })

        // Handle click outside
        const handleClickOutside = (e) => {
            if (e.target !== input) {
                handleEnter()
                document.removeEventListener('click', handleClickOutside)
            }
        }

        // Add click outside listener after a brief delay to prevent immediate triggering
        setTimeout(() => {
            document.addEventListener('click', handleClickOutside)
        }, 100)
    }

    // Setup click handler for text placement
    const handleCanvasClick = (e) => {
        const rect = canvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top

        const input = createTextInput(x, y)
        handleTextInput(input, x, y)
    }

    // Add click listener
    canvas.addEventListener('click', handleCanvasClick)

    // Store the handler for cleanup
    canvas.textClickHandler = handleCanvasClick

    // Update text color when color picker changes
    const popup = canvas.closest('.editable-popup')
    const colorPicker = popup?.querySelector('#colorPicker')
    if (colorPicker) {
        colorPicker.addEventListener('input', (e) => {
            TEXT_CONFIG.fillStyle = e.target.value
        })
    }
}

document.addEventListener("DOMContentLoaded", initializeEditableImages)
